import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ForbiddenProvider, useForbidden } from "./contexts/ForbiddenContext";
import { setGlobal403Handler } from "./services/api";
import { useEffect } from "react";
import GoogleAuthProvider from "./components/auth/GoogleAuthProvider";
import AuthGuard from "./guards/AuthGuard";
import Layout from "./components/layout/Layout";

// Pages
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Organization from "./pages/Organization";
import Users from "./pages/Users";
import Consumers from "./pages/Consumers";
import Services from "./pages/Services";
import Collection from "./pages/Collection";
import Voucher from "./pages/Voucher";
import Payments from "./pages/Payments";
import Logs from "./pages/Logs";
import NotFound from "./pages/NotFound";

// Masters
import Accounts from "./pages/masters/Accounts";
import Groups from "./pages/masters/Groups";
import MasterAccount from "./pages/masters/MasterAccount";
import Categories from "./pages/masters/Categories";
import Areas from "./pages/masters/Areas";
import Divisions from "./pages/masters/Divisions";
import Zones from "./pages/masters/Zones";

// Reports
import CollectionSummary from "./pages/reports/CollectionSummary";
import DueAnalysis from "./pages/reports/DueAnalysis";
import AccountStatement from "./pages/reports/AccountStatement";
import PaymentTrends from "./pages/reports/PaymentTrends";
import CollectorPerformance from "./pages/reports/CollectorPerformance";
import VoucherReport from "./pages/reports/VoucherReport";
import CollectionConsumerReport from "./pages/reports/CollectionConsumerReport";
import CollectionServiceReport from "./pages/reports/CollectionServiceReport";
import RecievableReport from "./pages/reports/RecievableReport";

const queryClient = new QueryClient();

// AuthLayout component to wrap protected routes
const AuthLayout = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard>
    <Layout>{children}</Layout>
  </AuthGuard>
);

// Component to set up the global 403 handler
const ForbiddenHandlerSetup = () => {
  const { showForbiddenModal } = useForbidden();

  useEffect(() => {
    // Set the global 403 handler
    setGlobal403Handler(showForbiddenModal);
  }, [showForbiddenModal]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <GoogleAuthProvider>
      <AuthProvider>
        <ForbiddenProvider>
          <ForbiddenHandlerSetup />
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/resetpassword/:token" element={<ResetPassword />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <AuthLayout>
                    <Dashboard />
                  </AuthLayout>
                }
              />
              <Route
                path="/organization"
                element={
                  <AuthLayout>
                    <Organization />
                  </AuthLayout>
                }
              />
              <Route
                path="/users"
                element={
                  <AuthLayout>
                    <Users />
                  </AuthLayout>
                }
              />
              <Route
                path="/consumers"
                element={
                  <AuthLayout>
                    <Consumers />
                  </AuthLayout>
                }
              />
              <Route
                path="/services"
                element={
                  <AuthLayout>
                    <Services />
                  </AuthLayout>
                }
              />
              <Route
                path="/collection"
                element={
                  <AuthLayout>
                    <Navigate to="/collection/record-payment" replace />
                  </AuthLayout>
                }
              />
              <Route
                path="/collection/record-payment"
                element={
                  <AuthLayout>
                    <Collection activeTab="payments" />
                  </AuthLayout>
                }
              />
              <Route
                path="/collection/configuration"
                element={
                  <AuthLayout>
                    <Collection activeTab="configs" />
                  </AuthLayout>
                }
              />
              <Route
                path="/collection/all-payments"
                element={
                  <AuthLayout>
                    <Payments />
                  </AuthLayout>
                }
              />
              <Route
                path="/voucher"
                element={
                  <AuthLayout>
                    <Navigate to="/voucher/payment" replace />
                  </AuthLayout>
                }
              />
              <Route
                path="/voucher/payment"
                element={
                  <AuthLayout>
                    <Voucher isReceipt={false} />
                  </AuthLayout>
                }
              />
              <Route
                path="/voucher/receipt"
                element={
                  <AuthLayout>
                    <Voucher isReceipt={true} />
                  </AuthLayout>
                }
              />
              <Route
                path="/payments"
                element={
                  <AuthLayout>
                    <Payments />
                  </AuthLayout>
                }
              />

              {/* Masters routes */}
              <Route
                path="/masters/accounts"
                element={
                  <AuthLayout>
                    <Accounts />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/groups"
                element={
                  <AuthLayout>
                    <Groups />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/master-account"
                element={
                  <AuthLayout>
                    <MasterAccount />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/categories"
                element={
                  <AuthLayout>
                    <Categories />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/areas"
                element={
                  <AuthLayout>
                    <Areas />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/divisions"
                element={
                  <AuthLayout>
                    <Divisions />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/zones"
                element={
                  <AuthLayout>
                    <Zones />
                  </AuthLayout>
                }
              />
              <Route
                path="/masters/*"
                element={
                  <AuthLayout>
                    <div className="text-center p-10">Masters Module</div>
                  </AuthLayout>
                }
              />

              {/* Reports routes */}
              <Route
                path="/reports/collection-summary"
                element={
                  <AuthLayout>
                    <CollectionSummary />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/due-analysis"
                element={
                  <AuthLayout>
                    <DueAnalysis />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/account-statement"
                element={
                  <AuthLayout>
                    <AccountStatement />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/payment-trends"
                element={
                  <AuthLayout>
                    <PaymentTrends />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/collector-performance"
                element={
                  <AuthLayout>
                    <CollectorPerformance />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/voucher-report"
                element={
                  <AuthLayout>
                    <VoucherReport />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/collection-consumer"
                element={
                  <AuthLayout>
                    <CollectionConsumerReport />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/collection-service"
                element={
                  <AuthLayout>
                    <CollectionServiceReport />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/recievable"
                element={
                  <AuthLayout>
                    <RecievableReport />
                  </AuthLayout>
                }
              />
              <Route
                path="/reports/*"
                element={
                  <AuthLayout>
                    <div className="text-center p-10">Reports Module</div>
                  </AuthLayout>
                }
              />

              {/* Logs route */}
              <Route
                path="/logs"
                element={
                  <AuthLayout>
                    <Logs />
                  </AuthLayout>
                }
              />

              {/* Redirect from / to /dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          </TooltipProvider>
        </ForbiddenProvider>
      </AuthProvider>
    </GoogleAuthProvider>
  </QueryClientProvider>
);

export default App;
